import { useState, useRef } from 'react';
import { FaCloudUploadAlt, FaTrash, FaSpinner, FaFileImage, FaFilePdf, FaExpand } from 'react-icons/fa';
import toast from 'react-hot-toast';
import './FileUpload.css';

/**
 * Derives the backend base URL from VITE_API_URL (which points to /graphql).
 * E.g. "http://localhost:4005/graphql" → "http://localhost:4005"
 */
function getBaseUrl() {
  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4005/graphql';
  return apiUrl.replace(/\/graphql$/, '');
}

/**
 * Reusable file upload component for attaching images/PDFs.
 *
 * Props:
 *  - value: current comprobante_url (string | null)
 *  - onChange: callback(url) when file is uploaded or removed
 *  - label: label text (default "Comprobante")
 */
const FileUpload = ({ value, onChange, label = 'Comprobante' }) => {
  const [uploading, setUploading] = useState(false);
  const [lightbox, setLightbox] = useState(false);
  const inputRef = useRef(null);

  const baseUrl = getBaseUrl();

  const fullUrl = value
    ? (value.startsWith('http') ? value : `${baseUrl}${value}`)
    : null;

  const isPdf = value && value.toLowerCase().endsWith('.pdf');

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('El archivo no debe superar 5MB');
      return;
    }

    // Validate type
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    if (!allowed.includes(file.type)) {
      toast.error('Tipo de archivo no permitido. Use: JPG, PNG, WEBP o PDF');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const token = localStorage.getItem('token');
      const res = await fetch(`${baseUrl}/api/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Error ${res.status}`);
      }

      const data = await res.json();
      onChange(data.url);
      toast.success('Archivo subido correctamente');
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error(error.message || 'Error al subir el archivo');
    } finally {
      setUploading(false);
      // Reset input so same file can be re-selected
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleRemove = () => {
    onChange(null);
  };

  return (
    <div className="file-upload">
      <label className="file-upload__label">{label}</label>

      {!value ? (
        /* Upload area */
        <div
          className={`file-upload__dropzone ${uploading ? 'file-upload__dropzone--uploading' : ''}`}
          onClick={() => !uploading && inputRef.current?.click()}
        >
          {uploading ? (
            <>
              <FaSpinner className="file-upload__spinner" />
              <span>Subiendo...</span>
            </>
          ) : (
            <>
              <FaCloudUploadAlt className="file-upload__icon" />
              <span>Click para seleccionar archivo</span>
              <small>JPG, PNG, WEBP o PDF (max 5MB)</small>
            </>
          )}
          <input
            ref={inputRef}
            type="file"
            accept=".jpg,.jpeg,.png,.webp,.pdf"
            onChange={handleFileSelect}
            className="file-upload__input"
          />
        </div>
      ) : (
        /* Preview */
        <div className="file-upload__preview">
          {isPdf ? (
            <div className="file-upload__pdf-icon">
              <FaFilePdf />
              <span>PDF adjunto</span>
            </div>
          ) : (
            <img
              src={fullUrl}
              alt="Comprobante"
              className="file-upload__image"
              onClick={() => setLightbox(true)}
            />
          )}
          <div className="file-upload__actions">
            {!isPdf && (
              <button
                type="button"
                className="file-upload__btn file-upload__btn--view"
                onClick={() => setLightbox(true)}
                title="Ver imagen completa"
              >
                <FaExpand />
              </button>
            )}
            {isPdf && (
              <a
                href={fullUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="file-upload__btn file-upload__btn--view"
                title="Abrir PDF"
              >
                <FaExpand />
              </a>
            )}
            <button
              type="button"
              className="file-upload__btn file-upload__btn--change"
              onClick={() => inputRef.current?.click()}
              title="Cambiar archivo"
            >
              <FaFileImage />
            </button>
            <button
              type="button"
              className="file-upload__btn file-upload__btn--remove"
              onClick={handleRemove}
              title="Eliminar archivo"
            >
              <FaTrash />
            </button>
          </div>
          <input
            ref={inputRef}
            type="file"
            accept=".jpg,.jpeg,.png,.webp,.pdf"
            onChange={handleFileSelect}
            className="file-upload__input"
          />
        </div>
      )}

      {/* Lightbox */}
      {lightbox && fullUrl && !isPdf && (
        <div className="file-upload__lightbox" onClick={() => setLightbox(false)}>
          <img src={fullUrl} alt="Comprobante" />
        </div>
      )}
    </div>
  );
};

export default FileUpload;
